/**
 * AuditIQ Visual Identity — Single Source of Truth
 * Any page, dashboard, report, or generated asset must import from here.
 */

export const BRAND_NAME = "AuditIQ";
export const BRAND_TAGLINE = "Governance · Cybersecurity · Infrastructure Transparency";

export const COLORS = {
  electric:    { name: "Electric Blue",  hex: "#2D9CDB" },
  navy:        { name: "AuditIQ Navy",  hex: "#0D1F3C" },
  navyDeep:    { name: "Navy Deep",      hex: "#081530" },
  navySoft:    { name: "Navy Soft",      hex: "#152A52" },
  gold:        { name: "AuditIQ Gold",  hex: "#C9A84C" },
  goldSoft:    { name: "Gold Soft",      hex: "#D9BC68" },
  goldDim:     { name: "Gold Dim",       hex: "#8A7333" },
  cream:       { name: "AuditIQ Cream", hex: "#F4EFE6" },
  creamDim:    { name: "Cream Dim",      hex: "#D8D2C5" },
  steel:       { name: "Steel Gray",     hex: "#4A5568" },
  success:     { name: "Success",        hex: "#2F855A" },
  warning:     { name: "Warning",        hex: "#ECC94B" },
  critical:    { name: "Critical",       hex: "#E53E3E" },
};

export const FONTS = {
  headingPrimary: "Cinzel",
  headingFallbacks: ["Libre Baskerville", "Trajan Pro", "Georgia", "serif"],
  bodyPrimary: "Inter",
  bodyFallbacks: ["Merriweather", "Georgia", "system-ui", "sans-serif"],
  monoPrimary: "JetBrains Mono",
};

export const TYPE_SCALE = {
  pageTitle:       { px: 48, weight: 700, family: "display", usage: "Hero / page titles" },
  sectionHeader:   { px: 32, weight: 600, family: "display", usage: "Section headings" },
  subheader:       { px: 24, weight: 500, family: "display", usage: "Panel titles" },
  dashboardMetric: { px: 36, weight: 600, family: "display", usage: "Dashboard KPI values" },
  bodyText:        { px: 18, weight: 400, family: "body",    usage: "Body copy" },
  smallText:       { px: 14, weight: 400, family: "body",    usage: "Labels, captions" },
  buttonText:      { px: 18, weight: 600, family: "body",    usage: "Buttons" },
};

export const SPACING = {
  grid: 12,
  columnGap: 24,
  sectionGap: 96,
  pageGutter: 24,
};

export const ICON_RULES = {
  color: "#C9A84C",       // Gold
  strokeWidth: 1.75,      // Consistent thickness
  style: "flat",          // Minimal, flat, non-dimensional
  corner: "round",
  sizes: [16, 20, 24, 32, 48, 64],
  categories: [
    "cybersecurity", "infrastructure", "government", "budget",
    "water", "risk-alert", "sensors", "ai", "data-monitoring", "compliance",
  ],
};

export const BUTTON_SPEC = {
  background: COLORS.gold.hex,
  textColor: COLORS.navy.hex,
  border: "1px solid " + COLORS.gold.hex,
  radius: "6px",
  fontSize: 18,
  fontWeight: 600,
  shape: "rounded rectangle",
  hover: "slight brightness increase (+8%)",
};

export const LOGO_RULES = [
  "Always high resolution (≥ 2x display size)",
  "Never stretched, recolored, distorted, or rotated",
  "Minimum clear space = height of the shield",
  "On navy: gold+cream treatment | On cream: navy+gold treatment",
];

export const IMAGE_QUALITY = {
  min: { w: 1920, h: 1080 },
  preferred: { w: 3840, h: 2160 },
  formats: ["png", "jpg", "svg", "webp"],
  constraints: ["HD", "clear", "sharp", "professional", "non-cartoon", "non-pixelated"],
};

export const DASHBOARD_SPEC = {
  background: COLORS.navy.hex,
  accentLines: COLORS.gold.hex,
  text: COLORS.cream.hex,
  panelStyle: "structured, clean, spaced, executive",
};

export default {
  BRAND_NAME, BRAND_TAGLINE, COLORS, FONTS, TYPE_SCALE,
  SPACING, ICON_RULES, BUTTON_SPEC, LOGO_RULES, IMAGE_QUALITY, DASHBOARD_SPEC,
};
